import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Bell, Camera, Check, CreditCard, Download, ExternalLink, FileText, Heart, KeyRound, LogIn, LogOut, Mail,
  Monitor, Pause, Pencil, Play, ShieldCheck, Smartphone, Sparkles, Tablet, Trash2, TriangleAlert, Undo2, UserRound, Users,
} from "lucide-react";
import { Draw, EASE, HEART, Head, Ink } from "../bits";
import { Part } from "../Page";
import { FOUNDING, ME, PLANS, viewer, type Member, type Plan, type Role, type Viewer } from "./sample";
import { JoinButton, LockedRow, M, MembersPage, ReviewControls, joinCta, useRole, withRole } from "./Members";
import "../SectionsA.css";
import "./Account.css";

/* Member settings (/blue-2/members/account, also M.settings). One page with a
   section nav (a sticky side list on desktop, sticky chips on phones) and six
   sections: profile, membership & billing, notifications, privacy & community,
   login & security, and your data. Everything is local state on sample data:
   Stripe, email and downloads only show a toast saying what would happen.

   Review states: the role in the bottom review bar (visitor gets a sign-in
   card, Thread Friend the $3/mo plan plus an upgrade card, Chelsea her own
   settings plus the admin link), and a Billing switch in the same bar that
   shows the "Complimentary access" variant.

   This file also exports small pieces the Makes page uses (avatars, toasts,
   the dialog hook). ME is re-exported from sample.ts for older imports. */

export { ME };

/* ─── Shared bits (also used by Community.tsx) ─── */

const AV = ["soft", "pop", "extra", "strong"] as const;
export const initials = (name: string) => name.split(/\s+/).map(w => w[0]).join("").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();

/** A member's photo, or their initials on a palette color picked from their id. */
export function Avatar({ m, size = 36, className = "" }: { m: Member; size?: number; className?: string }) {
  if (m.avatar) return <img className={`ma-av ${className}`} src={m.avatar} alt="" width={size} height={size} style={{ width: size, height: size }} />;
  const n = [...m.id].reduce((a, c) => a + c.charCodeAt(0), 0) % AV.length;
  return <span className={`ma-av ma-av-${AV[n]} ${className}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }} aria-hidden="true">{initials(m.name)}</span>;
}

/** One toast at a time; the region is always mounted so screen readers hear it. */
export function useToast() {
  const [msg, setMsg] = useState<{ text: string; n: number } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((text: string) => {
    window.clearTimeout(timer.current);
    setMsg(m => ({ text, n: (m?.n ?? 0) + 1 }));
    timer.current = window.setTimeout(() => setMsg(null), 4200);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const node = <div className="ma-toast-region" role="status" aria-live="polite">
    <AnimatePresence>{msg && <motion.p key={msg.n} className="ma-toast" initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.3, ease: EASE }}>
      <Check size={16} aria-hidden="true" /> {msg.text}
    </motion.p>}</AnimatePresence>
  </div>;
  return { show, node };
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Modal behavior for an open dialog: focus moves in (to [data-autofocus] or
    the first control), Tab stays inside, Escape closes, the page behind stops
    scrolling, and focus returns to whatever opened it. */
export function useDialog<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    (el?.querySelector<HTMLElement>("[data-autofocus]") ?? el?.querySelector<HTMLElement>(FOCUSABLE))?.focus({ preventScroll: true });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); close.current(); return; }
      if (e.key !== "Tab" || !el) return;
      const f = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(x => x.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; prev?.focus?.({ preventScroll: true }); };
  }, [open]);
  return ref;
}

/* ─── Sample settings data ─── */

const COMP_UNTIL = "Jan 31, 2027";
const PAUSE_UNTIL = "Dec 3, 2026";
const NEXT = ME.nextBilling; // "Nov 3, 2026"
const CARD = "Visa •••• 4242";
const DAY = 864e5;
const PERIOD = { start: new Date(2026, 9, 3), end: new Date(2026, 10, 3) };

/** What upgrading to Member costs today: the price difference for the days left in this billing period. */
function proration(now = new Date()) {
  const total = Math.round((PERIOD.end.getTime() - PERIOD.start.getTime()) / DAY);
  const left = Math.min(total, Math.max(0, Math.ceil((PERIOD.end.getTime() - now.getTime()) / DAY)));
  return { left, total, amount: ((PLANS.member.price - PLANS.friend.price) * left) / total };
}
const money = (n: number) => `$${n.toFixed(2)}`;
const today = () => new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });

const INCLUDES: Record<Plan, string[]> = {
  member: ["Two live classes a month, 2nd Tuesday + the Thursday after", "Every class replay, with chapters", "Posts, polls & the makes wall", "Share makes and enter challenges"],
  friend: ["Chelsea’s posts and studio updates", "Browse and heart everyone’s makes", "A warm fuzzy feeling (and Cosmo’s thanks)"],
};

type Device = { id: string; kind: "desktop" | "phone" | "tablet"; name: string; where: string; when: string; current?: boolean };
const DEVICES: Device[] = [
  { id: "d1", kind: "desktop", name: "Chrome on Mac", where: "Denver, CO", when: "Active now", current: true },
  { id: "d2", kind: "phone", name: "Safari on iPhone", where: "Denver, CO", when: "2 hours ago" },
  { id: "d3", kind: "tablet", name: "Safari on iPad", where: "Boulder, CO", when: "Oct 2" },
  { id: "d4", kind: "desktop", name: "Firefox on Windows", where: "Salt Lake City, UT", when: "Sep 21" },
];
const BLOCKED: (Member & { when: string })[] = [
  { id: "b1", name: "Riley M.", since: "2026", when: "Blocked Sep 14" },
  { id: "b2", name: "Toni V.", since: "2025", when: "Blocked Aug 2" },
];

/* ─── Page state ─── */

type Profile = { name: string; bio: string; photo?: string };
type Bill = { plan: Plan; comp: boolean; pending?: Plan; paused?: boolean; canceled?: boolean };
type Privacy = { makes: boolean; chatName: boolean; profile: boolean };
type PanelKey = "idle" | "switch" | "pause" | "cancel";

const BIO = {
  me: "Scrap quilter in Denver. Two cats, one very patient sewing machine, and a tote habit.",
  chelsea: "Teacher, host & chief scrap hoarder. Cosmo supervises.",
};
const initProfile = (who: Viewer): Profile => ({ name: who.name, bio: who.id === "chelsea" ? BIO.chelsea : BIO.me, photo: who.avatar });
const initBill = (role: Role): Bill => ({ plan: role === "friend" ? "friend" : "member", comp: false });
const roleFromUrl = (): Role => {
  const r = new URLSearchParams(window.location.search).get("as");
  return r === "visitor" || r === "friend" || r === "chelsea" ? r : "member";
};

type Store = {
  profile: Profile; setProfile: (p: Profile) => void;
  bill: Bill; setBill: (b: Bill) => void;
  panel: PanelKey; setPanel: (p: PanelKey) => void;
  privacy: Privacy; setPrivacy: (p: Privacy) => void;
  email: string; setEmail: (e: string) => void;
  toast: (t: string) => void; goTo: (id: SectionId) => void;
};
const S = createContext<Store>(null!);
const useStore = () => useContext(S);

/* ─── Sections and nav ─── */

const SECTIONS = [
  { id: "profile", label: "Profile", short: "Profile", icon: UserRound },
  { id: "billing", label: "Membership & billing", short: "Billing", icon: CreditCard },
  { id: "notifications", label: "Notifications", short: "Notifications", icon: Bell },
  { id: "privacy", label: "Privacy & community", short: "Privacy", icon: ShieldCheck },
  { id: "security", label: "Login & security", short: "Security", icon: KeyRound },
  { id: "data", label: "Your data", short: "Your data", icon: Download },
] as const;
type SectionId = (typeof SECTIONS)[number]["id"];
const sectionLabel = (id: SectionId, chelsea: boolean) => (chelsea && id === "billing" ? "Club & payouts" : SECTIONS.find(s => s.id === id)!.label);

/** Scroll-spy: the section whose top has crossed a line a third of the way down the viewport. */
function useActiveSection() {
  const [active, setActive] = useState<SectionId>("profile");
  useEffect(() => {
    const els = SECTIONS.map(s => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    if (!els.length) return;
    const pick = () => {
      const line = window.innerHeight * 0.34;
      let cur: SectionId = SECTIONS[0].id;
      for (const el of els) if (el.getBoundingClientRect().top <= line) cur = el.id as SectionId;
      // At the very bottom, the last section wins even if it's short.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        const last = els[els.length - 1];
        if (last.getBoundingClientRect().top < window.innerHeight) cur = last.id as SectionId;
      }
      setActive(cur);
    };
    pick();
    window.addEventListener("scroll", pick, { passive: true });
    window.addEventListener("resize", pick);
    return () => { window.removeEventListener("scroll", pick); window.removeEventListener("resize", pick); };
  }, []);
  return active;
}

function SettingsNav({ active, chelsea }: { active: SectionId; chelsea: boolean }) {
  const { goTo } = useStore();
  const strip = useRef<HTMLUListElement>(null);
  const [edge, setEdge] = useState({ less: false, more: false });
  // On phones the chip strip scrolls sideways: keep the current chip in view and fade the edges.
  useLayoutEffect(() => {
    const el = strip.current; if (!el) return;
    const check = () => setEdge({ less: el.scrollLeft > 2, more: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check); ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, []);
  useEffect(() => {
    const el = strip.current; if (!el || el.scrollWidth <= el.clientWidth) return;
    const chip = el.querySelector<HTMLElement>(`[data-id="${active}"]`);
    if (chip) el.scrollTo({ left: Math.max(0, chip.offsetLeft - (el.clientWidth - chip.offsetWidth) / 2), behavior: "smooth" });
  }, [active]);
  return <nav className={`ma-nav${edge.less ? " is-less" : ""}${edge.more ? " is-more" : ""}`} aria-label="Settings sections">
    <p className="ma-nav-title" aria-hidden="true">Settings</p>
    <ul ref={strip}>
      {SECTIONS.map(s => { const Icon = s.icon; const label = sectionLabel(s.id, chelsea);
        return <li key={s.id} data-id={s.id}>
          <a href={`#${s.id}`} aria-current={active === s.id ? "location" : undefined} onClick={e => { e.preventDefault(); goTo(s.id); }}>
            <Icon size={17} aria-hidden="true" /><span className="ma-nav-long">{label}</span><span className="ma-nav-short" aria-hidden="true">{chelsea && s.id === "billing" ? "Club" : s.short}</span>
          </a>
        </li>; })}
    </ul>
  </nav>;
}

/** One settings section: a card with an icon, title and a line of what's in it. */
function Section({ id, title, lede, children, tone }: { id: SectionId; title: string; lede: ReactNode; children: ReactNode; tone?: "danger" }) {
  const Icon = SECTIONS.find(s => s.id === id)!.icon;
  return <section id={id} className={`ma-set${tone ? ` ma-set-${tone}` : ""}`} aria-labelledby={`${id}-t`}>
    <header className="ma-set-head">
      <span className="ma-set-icon" aria-hidden="true"><Icon size={22} /></span>
      <div>
        <h3 id={`${id}-t`} className="ma-set-title" tabIndex={-1}>{title}</h3>
        <p className="ma-set-lede">{lede}</p>
      </div>
    </header>
    {children}
  </section>;
}

/** A sub-part of a section, with a small heading. */
function Block({ title, aside, children, className = "" }: { title: string; aside?: ReactNode; children: ReactNode; className?: string }) {
  const id = useId();
  return <div className={`ma-block ${className}`} role="group" aria-labelledby={id}>
    <div className="ma-block-top"><h4 id={id} className="ma-block-title">{title}</h4>{aside}</div>
    {children}
  </div>;
}

/** Height-animated reveal for inline panels (respects reduced motion). */
function Reveal({ open, id, children }: { open: boolean; id?: string; children: ReactNode }) {
  const reduce = useReducedMotion();
  return <AnimatePresence initial={false}>
    {open && <motion.div id={id} className="ma-reveal" initial={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }} animate={reduce ? { opacity: 1 } : { opacity: 1, height: "auto" }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }} transition={{ duration: 0.32, ease: EASE }}>{children}</motion.div>}
  </AnimatePresence>;
}

/** Focus the first [data-autofocus] inside a panel when it opens. */
function useAutofocus<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => { if (open) requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus()); }, [open]);
  return ref;
}

/** A bare on/off switch. Label it with aria-labelledby (or aria-label). */
function Toggle({ on, onChange, disabled, labelledBy, label, describedBy, small }: {
  on: boolean; onChange: (v: boolean) => void; disabled?: boolean; labelledBy?: string; label?: string; describedBy?: string; small?: boolean;
}) {
  return <button type="button" role="switch" className={`ma-switch${small ? " ma-switch-sm" : ""}`} aria-checked={on && !disabled} aria-labelledby={labelledBy} aria-label={label} aria-describedby={describedBy}
    disabled={disabled} onClick={() => onChange(!on)}>
    <span className="ma-switch-knob" aria-hidden="true">{on && !disabled ? <Check size={small ? 11 : 13} strokeWidth={3} /> : null}</span>
  </button>;
}

/** A switch with its label and hint (and an optional lock line or extra under the hint). */
function SwitchRow({ label, hint, on, onChange, disabled, extra }: { label: string; hint: ReactNode; on: boolean; onChange: (v: boolean) => void; disabled?: boolean; extra?: ReactNode }) {
  const id = useId();
  return <li className={`ma-pref${disabled ? " is-off" : ""}`}>
    <div className="ma-pref-text">
      <span id={`${id}-l`} className="ma-pref-label">{label}</span>
      <span id={`${id}-h`} className="ma-pref-hint">{hint}</span>
      {extra}
    </div>
    <Toggle on={on} onChange={onChange} disabled={disabled} labelledBy={`${id}-l`} describedBy={`${id}-h`} />
  </li>;
}

/** Inline "Saved." line for settings that save as you change them. */
function Saved({ text }: { text: string }) {
  return <p className="ma-saved" role="status" aria-live="polite">{text && <><Check size={14} strokeWidth={3} aria-hidden="true" /> {text}</>}</p>;
}

/* ─── Header ─── */

function Title({ hand, word }: { hand: string; word: string }) {
  return <h1 className="ma-title">
    <motion.span className="ma-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.35, duration: 0.5, ease: EASE }}>{hand}</motion.span>
    <span className={`ma-title-word${/[gjpqy]/.test(word) ? " ma-title-desc" : ""}`} aria-label={word}>
      {word.split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
    </span>
  </h1>;
}

/** Plan line used on the member card and the plan ticket. */
function planLine(bill: Bill) {
  if (bill.comp) return `${PLANS[bill.plan].name} · complimentary`;
  if (bill.canceled) return `${PLANS[bill.plan].name} · ends ${NEXT}`;
  if (bill.paused) return `${PLANS[bill.plan].name} · paused`;
  return `${PLANS[bill.plan].name} · ${PLANS[bill.plan].per}`;
}

/** The member card in the header: who you are in the club (the saved profile). */
function MemberCard({ who, chelsea }: { who: Viewer; chelsea: boolean }) {
  const { profile, bill, goTo } = useStore();
  const shown: Member = { ...who, name: profile.name, avatar: profile.photo };
  const friend = !chelsea && bill.plan === "friend";
  return <motion.div className={`ma-card${friend ? " ma-card-friend" : ""}`} initial={{ opacity: 0, y: 30, rotate: 7 }} animate={{ opacity: 1, y: 0, rotate: 2.5 }} transition={{ delay: 0.35, type: "spring", stiffness: 120, damping: 15 }}>
    <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
    <p className="ma-card-top">Loose Threads Club · {chelsea ? "Founder" : `No. ${who.no}`}</p>
    <div className="ma-card-main">
      <span className="ma-card-av"><Avatar m={shown} size={92} /></span>
      <div className="ma-card-id">
        <p className="ma-card-name">{profile.name}</p>
        <p className="ma-card-since">{chelsea ? "Teacher, host & chief scrap hoarder" : <>{planLine(bill)}<br />In the club since {who.joined}</>}</p>
        <a href="#profile" className="ma-card-editbtn" onClick={e => { e.preventDefault(); goTo("profile"); }}><Pencil size={13} aria-hidden="true" /> Edit profile</a>
      </div>
    </div>
    <div className="ma-card-foot">
      <span className="ma-stitch" aria-hidden="true" />
      {chelsea ? <span className="ma-badge ma-badge-chelsea"><Sparkles size={14} aria-hidden="true" /> It’s your club</span>
        : who.founding && <span className="ma-badge"><Heart size={13} fill="currentColor" aria-hidden="true" /> {FOUNDING}</span>}
    </div>
    {!chelsea && who.founding && <svg className="ma-card-rosette" viewBox="0 0 80 80" role="img" aria-label={`${FOUNDING} badge, ${who.since}`}>
      <circle cx="40" cy="40" r="30" className="ma-rosette-fill" />
      <circle cx="40" cy="40" r="24" className="ma-rosette-ring" />
      <text x="40" y="37" textAnchor="middle">founding</text><text x="40" y="51" textAnchor="middle">{who.since}</text>
    </svg>}
  </motion.div>;
}

function Header() {
  const role = useRole();
  const { goTo } = useStore();
  if (role === "visitor") return <div className="ma-head">
    <div className="ma-head-copy">
      <motion.p className="ma-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · Settings</motion.p>
      <Title hand="welcome" word="back" />
      <motion.p className="ma-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.45, ease: EASE }}>
        Sign in to change your profile, plan, emails and privacy. We’ll email you a sign-in link, so there’s nothing to remember.
      </motion.p>
    </div>
    <SignIn />
  </div>;
  const chelsea = role === "chelsea";
  const who = viewer(role)!;
  return <div className="ma-head">
    <div className="ma-head-copy">
      <motion.p className="ma-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · Settings</motion.p>
      <Title hand={chelsea ? "it's your" : "it's all about"} word={chelsea ? "club" : "you"} />
      <motion.p className="ma-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.45, ease: EASE }}>
        {chelsea ? "Your profile, payouts, emails and sign-in, all in one spot. Members, comps and staff roles live in the club admin."
          : "Your profile, plan, emails and privacy, all in one spot. Change anything, anytime."}
      </motion.p>
      <motion.div className="ma-head-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.45, ease: EASE }}>
        <a href="#billing" className="mb-btn mb-btn-hero" onClick={e => { e.preventDefault(); goTo("billing"); }}>{chelsea ? "Club & payouts" : "Your plan"} <ArrowRight size={16} aria-hidden="true" /></a>
        <a href="#notifications" className="mb-btn mb-btn-line" onClick={e => { e.preventDefault(); goTo("notifications"); }}>Notifications</a>
      </motion.div>
    </div>
    <MemberCard who={who} chelsea={chelsea} />
  </div>;
}

/** Visitors: a prototype sign-in by email link. */
function SignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setErr("That email doesn’t look quite right."); return; }
    setErr(""); setSent(true);
  };
  return <motion.div className="ma-signin" initial={{ opacity: 0, y: 30, rotate: 6 }} animate={{ opacity: 1, y: 0, rotate: 1.5 }} transition={{ delay: 0.35, type: "spring", stiffness: 120, damping: 15 }}>
    <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
    <AnimatePresence mode="wait" initial={false}>
      {sent
        ? <motion.div key="sent" className="ma-signin-sent" role="status" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <span className="ma-signin-icon"><Mail size={26} aria-hidden="true" /></span>
            <h2>Check your inbox</h2>
            <p>We sent a sign-in link to <b>{email.trim()}</b>. It works for 15 minutes.</p>
            <p className="ma-proto">Prototype: no email was actually sent.</p>
            <button type="button" className="ma-link" onClick={() => setSent(false)}>Use a different email</button>
          </motion.div>
        : <motion.form key="form" onSubmit={submit} noValidate initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <h2 className="ma-signin-title">Sign in to the club</h2>
            <label htmlFor="ma-email" className="ma-field-label">Email</label>
            <input id="ma-email" type="email" autoComplete="email" inputMode="email" className="ma-input" placeholder="you@example.com" value={email}
              aria-invalid={!!err} aria-describedby={err ? "ma-email-err" : "ma-email-hint"} onChange={e => setEmail(e.target.value)} />
            {err ? <p id="ma-email-err" className="ma-err">{err}</p> : <p id="ma-email-hint" className="ma-hint">Coming from Patreon? Use the email on your Patreon account and we’ll find your membership.</p>}
            <button type="submit" className="mb-btn mb-btn-ink mb-btn-wide"><LogIn size={17} aria-hidden="true" /> Email me a sign-in link</button>
          </motion.form>}
    </AnimatePresence>
  </motion.div>;
}

/* ─── 1. Profile ─── */

function ProfileSection({ who, chelsea }: { who: Viewer; chelsea: boolean }) {
  const { profile, setProfile, privacy, toast } = useStore();
  const [draft, setDraft] = useState<Profile>(profile);
  const [err, setErr] = useState("");
  const urls = useRef<string[]>([]);
  useEffect(() => setDraft(profile), [profile]);
  // Photos picked here are object URLs; free them when the page goes away.
  useEffect(() => () => urls.current.forEach(u => URL.revokeObjectURL(u)), []);
  const dirty = draft.name !== profile.name || draft.bio !== profile.bio || draft.photo !== profile.photo;
  const pickPhoto = (f?: File) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("That file isn’t a photo. Try a JPG, PNG or HEIC."); return; }
    if (f.size > 8 * 1024 * 1024) { setErr("That photo is over 8 MB. Try a smaller one."); return; }
    const url = URL.createObjectURL(f);
    urls.current.push(url);
    setErr(""); setDraft(d => ({ ...d, photo: url }));
  };
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name) { setErr("Your display name can’t be empty."); return; }
    setErr(""); setProfile({ ...draft, name, bio: draft.bio.trim() });
    toast("Profile saved. Your comments and makes show the new details.");
  };
  const preview: Member = { ...who, name: draft.name.trim() || profile.name, avatar: draft.photo };
  const BIO_MAX = 160;
  return <Section id="profile" title="Profile" lede="How you show up on posts, the makes wall and in live chat.">
    <form className="ma-profile" onSubmit={save} noValidate>
      <div className="ma-profile-form">
        <div className="ma-photo">
          <span className="ma-photo-av"><Avatar m={preview} size={84} /></span>
          <div className="ma-photo-actions">
            <label className="mb-btn mb-btn-line mb-btn-sm ma-file">
              <Camera size={16} aria-hidden="true" /> {draft.photo ? "Change photo" : "Upload a photo"}
              <input type="file" accept="image/*" className="mb-sr" onChange={e => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {draft.photo && <button type="button" className="ma-link" onClick={() => setDraft(d => ({ ...d, photo: undefined }))}>Remove photo</button>}
            <p className="ma-hint ma-hint-tight">Square works best. Your initials show if there’s no photo.</p>
          </div>
        </div>
        <div className="ma-field">
          <label htmlFor="ma-p-name" className="ma-field-label">Display name</label>
          <input id="ma-p-name" className="ma-input" value={draft.name} maxLength={28} autoComplete="nickname" aria-describedby="ma-p-name-h"
            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
          <p id="ma-p-name-h" className="ma-hint ma-hint-tight">First name and an initial is the club’s habit, like “Katie B.”</p>
        </div>
        <div className="ma-field">
          <label htmlFor="ma-p-bio" className="ma-field-label">Short bio <span className="ma-optional">optional</span></label>
          <textarea id="ma-p-bio" className="ma-input ma-textarea" rows={3} maxLength={BIO_MAX} value={draft.bio} aria-describedby="ma-p-bio-h"
            onChange={e => setDraft(d => ({ ...d, bio: e.target.value }))} />
          <p id="ma-p-bio-h" className="ma-hint ma-hint-tight ma-count-line"><span>What you sew, where you sew it.</span><span>{draft.bio.length}/{BIO_MAX}</span></p>
        </div>
        <dl className="ma-facts">
          <div><dt>{chelsea ? "Club founder" : "Member since"}</dt><dd>{who.joined}{!chelsea && <> · No. {who.no}</>}</dd></div>
          <div><dt>Badge</dt><dd>{chelsea ? <span className="ma-badge ma-badge-chelsea"><Sparkles size={13} aria-hidden="true" /> Founder</span>
            : who.founding ? <span className="ma-badge"><Heart size={12} fill="currentColor" aria-hidden="true" /> {FOUNDING}</span> : "None yet"}</dd></div>
        </dl>
        {err && <p className="ma-err" role="alert">{err}</p>}
        <div className="ma-actions">
          <button type="submit" className="mb-btn mb-btn-ink" disabled={!dirty}>Save profile</button>
          {dirty && <button type="button" className="mb-btn mb-btn-line" onClick={() => { setDraft(profile); setErr(""); }}>Discard changes</button>}
          {!dirty && <span className="ma-quiet">All saved</span>}
        </div>
      </div>

      <figure className="ma-preview" aria-label="Public profile preview">
        <figcaption className="ma-preview-cap">What members see <span aria-hidden="true">↓</span></figcaption>
        <div className="ma-preview-card">
          <Avatar m={preview} size={64} />
          <p className="ma-preview-name">{preview.name}</p>
          {chelsea ? <span className="ma-badge ma-badge-chelsea"><Sparkles size={13} aria-hidden="true" /> Founder</span>
            : who.founding && <span className="ma-badge"><Heart size={12} fill="currentColor" aria-hidden="true" /> {FOUNDING}</span>}
          {privacy.profile || chelsea
            ? <>
                <p className="ma-preview-bio">{draft.bio.trim() || <em>No bio yet.</em>}</p>
                <ul className="ma-preview-stats">
                  <li><b>{chelsea ? "48" : who.plan === "friend" ? "0" : "7"}</b> {chelsea ? "classes" : "makes"}</li>
                  <li><b>{chelsea ? "312" : "41"}</b> comments</li>
                  <li>since <b>{who.joined.split(" ").pop()}</b></li>
                </ul>
              </>
            : <p className="ma-preview-hidden"><ShieldCheck size={15} aria-hidden="true" /> Your profile is private, so members only see your name and photo.</p>}
        </div>
        <p className="ma-preview-note">Updates as you type. Only signed-in club members can open profiles.</p>
      </figure>
    </form>
  </Section>;
}

/* ─── 2. Membership & billing ─── */

function UpgradeCard() {
  const role = useRole();
  const { bill, setPanel, goTo } = useStore();
  const c = joinCta(role, "members");
  if (!c || bill.plan !== "friend" || bill.comp) return null;
  return <aside className="ma-upgrade" aria-labelledby="ma-up-t">
    <svg className="ma-upgrade-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d={HEART} width={3.2} /></svg>
    <div>
      <p className="ma-kick">You’re a {PLANS.friend.name}, thank you!</p>
      <h3 id="ma-up-t" className="ma-upgrade-title">Want the classes too?</h3>
      <p className="ma-upgrade-text">{PLANS.member.name} adds two live sew-alongs a month, every replay with chapters, and sharing your makes. You’d pay {money(proration().amount)} today for the rest of this month, then {PLANS.member.per}.</p>
    </div>
    <button type="button" className="mb-btn mb-btn-ink" onClick={() => { setPanel("switch"); goTo("billing"); }}>{c.label} <ArrowRight size={16} aria-hidden="true" /></button>
  </aside>;
}

function PlanTicket() {
  const { bill, setBill, setPanel, panel, toast } = useStore();
  const { plan, comp, pending, paused, canceled } = bill;
  const card = useRef<HTMLDivElement>(null);
  const refocus = () => requestAnimationFrame(() => card.current?.querySelector<HTMLElement>(".ma-ticket-actions .mb-btn")?.focus());
  const chip = comp ? <span className="ma-chip ma-chip-comp">Complimentary</span>
    : canceled ? <span className="ma-chip ma-chip-warn">Ends {NEXT}</span>
    : paused ? <span className="ma-chip ma-chip-warn">Paused</span>
    : pending ? <span className="ma-chip ma-chip-warn">Switching {NEXT}</span>
    : <span className="ma-chip">Active</span>;
  const status = comp ? <>Free access from the Loose Threads team until <b>{COMP_UNTIL}</b></>
    : canceled ? <>Canceled · access until {NEXT}</>
    : paused ? <>Paused until {PAUSE_UNTIL}</>
    : pending ? <>Active · {PLANS[pending].name} from {NEXT}</>
    : <>Active, renews monthly</>;
  const next = comp ? `None until ${COMP_UNTIL}` : canceled ? "None" : paused ? `${money(PLANS[plan].price)} on ${PAUSE_UNTIL}` : `${money(PLANS[pending ?? plan].price)} on ${NEXT}`;
  const open = (p: PanelKey) => setPanel(panel === p ? "idle" : p);
  return <div className="ma-ticket" ref={card}>
    <div className="ma-ticket-stub" aria-hidden="true">
      <b>{comp ? "$0" : `$${PLANS[plan].price}`}</b><span>{comp ? "on the house" : "a month"}</span>
    </div>
    <div className="ma-ticket-body">
      <div className="ma-ticket-top"><p className="ma-kick">Your plan</p>{chip}</div>
      <h4 className="ma-plan-name">{PLANS[plan].name} <span className="mb-sr">, {comp ? "complimentary" : PLANS[plan].per}</span></h4>
      <ul className="ma-includes">{INCLUDES[plan].map(t => <li key={t}><Check size={16} strokeWidth={3} aria-hidden="true" /> {t}</li>)}</ul>
      <dl className="ma-bill">
        <div><dt>Status</dt><dd>{status}</dd></div>
        <div><dt>Next charge</dt><dd>{next}</dd></div>
        <div><dt>Card on file</dt><dd><CreditCard size={15} aria-hidden="true" /> {comp ? "None needed" : <>{CARD} · exp 08/28</>}</dd></div>
      </dl>
      {comp && <p className="ma-note-line"><Sparkles size={15} aria-hidden="true" /> Chelsea’s team gave you {PLANS[plan].name} access on the house. Nothing is charged. A week before {COMP_UNTIL} we’ll email to ask if you want to keep going.</p>}
      {pending && <p className="ma-note-line">From {NEXT} you’ll be a {PLANS.friend.name} at {PLANS.friend.per}. Classes and replays stay open until then.
        <button type="button" className="ma-link" onClick={() => { setBill({ plan, comp }); toast(`Staying a ${PLANS[plan].name}. Nothing changes on ${NEXT}.`); refocus(); }}><Undo2 size={14} aria-hidden="true" /> Undo</button></p>}
      {paused && <p className="ma-note-line">Taking a month off: no charge on {NEXT}. Posts stay open; classes and replays wait for you until {PAUSE_UNTIL}.
        <button type="button" className="ma-link" onClick={() => { setBill({ plan, comp }); toast("Welcome back! Your membership is running again."); refocus(); }}><Play size={14} aria-hidden="true" /> Resume now</button></p>}
      {canceled && <p className="ma-note-line">We’ll miss you! You keep access until {NEXT} and won’t be charged again. Your makes stay on the wall.
        <button type="button" className="ma-link" onClick={() => { setBill({ plan, comp }); toast("You’re back! Membership resumed."); refocus(); }}><Undo2 size={14} aria-hidden="true" /> Resume membership</button></p>}
      <div className="ma-ticket-actions">
        <button type="button" className="mb-btn mb-btn-ink" onClick={() => toast("Prototype: this opens the Stripe billing portal.")}>
          <ExternalLink size={16} aria-hidden="true" /> {comp ? "Add a card for later" : "Manage in Stripe"}
        </button>
        {!comp && !canceled && <button type="button" className="mb-btn mb-btn-line" aria-expanded={panel === "switch"} aria-controls="ma-plan-panel" onClick={() => open("switch")}>
          {plan === "friend" && !pending ? `Upgrade to ${PLANS.member.name}` : "Switch plan"}
        </button>}
        {!comp && !canceled && !paused && !pending && <button type="button" className="mb-btn mb-btn-line" aria-expanded={panel === "pause"} aria-controls="ma-plan-panel" onClick={() => open("pause")}>
          <Pause size={15} aria-hidden="true" /> Pause a month
        </button>}
      </div>
      {!comp && !canceled && <button type="button" className="ma-cancel-link" aria-expanded={panel === "cancel"} aria-controls="ma-plan-panel" onClick={() => open("cancel")}>Cancel membership</button>}
    </div>
  </div>;
}

/** The plan panels: switch tier, pause, cancel. */
function PlanPanel() {
  const { bill, setBill, panel, setPanel, toast } = useStore();
  const { plan, comp } = bill;
  const [pick, setPick] = useState<Plan>(plan === "member" ? "friend" : "member");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const ref = useAutofocus<HTMLDivElement>(panel !== "idle");
  useEffect(() => { if (panel === "switch") setPick(plan === "member" ? "friend" : "member"); if (panel === "cancel") { setReason(""); setNote(""); } }, [panel, plan]);
  const close = () => { setPanel("idle"); requestAnimationFrame(() => document.querySelector<HTMLElement>(".ma-ticket-actions .mb-btn")?.focus()); };
  const pr = proration();
  const confirmSwitch = () => {
    if (pick === plan) return close();
    if (pick === "member") { setBill({ plan: "member", comp }); toast(`Welcome to full membership! Charged ${money(pr.amount)} today. Classes and replays are open.`); }
    else { setBill({ plan, comp, pending: "friend" }); toast(`You’ll switch to ${PLANS.friend.name} on ${NEXT}.`); }
    close();
  };
  const pause = () => { setBill({ plan, comp, paused: true }); toast(`Paused. No charge on ${NEXT}; we’ll pick back up on ${PAUSE_UNTIL}.`); close(); };
  const cancel = () => { setBill({ plan, comp, canceled: true }); toast(`Canceled. You keep access until ${NEXT}.`); close(); };
  return <Reveal open={panel !== "idle"} id="ma-plan-panel">
    <div ref={ref} className="ma-panel-in" key={panel}>
      {panel === "switch" && <>
        <h4 className="ma-panel-title" tabIndex={-1} data-autofocus>Switch your plan</h4>
        <div className="ma-choose" role="radiogroup" aria-label="Plans">
          {(["member", "friend"] as Plan[]).map(p => <button key={p} type="button" role="radio" aria-checked={pick === p} className={`ma-option${pick === p ? " is-on" : ""}`} onClick={() => setPick(p)}>
            <span className="ma-option-top"><b>{PLANS[p].name}</b>{p === plan && <em>your plan</em>}</span>
            <span className="ma-option-price">${PLANS[p].price}<small>/mo</small></span>
            <span className="ma-option-text">{p === "member" ? "Live classes, every replay, makes and challenges." : "Posts and browsing makes. No classes or replays."}</span>
            <span className="ma-option-dot" aria-hidden="true">{pick === p && <Check size={14} strokeWidth={3} />}</span>
          </button>)}
        </div>
        <p className="ma-panel-note" aria-live="polite">
          {pick === plan ? "That’s the plan you’re on."
            : pick === "member" ? <>Starts today. You’ll pay <b>{money(pr.amount)}</b> now, the difference for the {pr.left} days left in this billing period (prorated), then {PLANS.member.per} from {NEXT}.</>
            : <>Switches on {NEXT}. Keep classes and replays until then, then it’s {PLANS.friend.per}. No partial refunds, so the rest of this month is still yours.</>}
        </p>
        <div className="ma-actions">
          <button type="button" className="mb-btn mb-btn-ink" onClick={confirmSwitch} disabled={pick === plan}>{pick === "member" ? `Upgrade now · ${money(pr.amount)} today` : `Switch on ${NEXT.replace(/, \d{4}$/, "")}`}</button>
          <button type="button" className="mb-btn mb-btn-line" onClick={close}>Never mind</button>
        </div>
      </>}
      {panel === "pause" && <>
        <h4 className="ma-panel-title" tabIndex={-1} data-autofocus>Take a month off</h4>
        <p className="ma-bye-text">Busy month? We’ll skip your {NEXT} charge and pick back up on {PAUSE_UNTIL}. While you’re paused, posts stay open and your makes stay up; classes and replays wait for you.</p>
        <div className="ma-actions">
          <button type="button" className="mb-btn mb-btn-ink" onClick={pause}><Pause size={15} aria-hidden="true" /> Pause until {PAUSE_UNTIL.replace(/, \d{4}$/, "")}</button>
          <button type="button" className="mb-btn mb-btn-line" onClick={close}>Never mind</button>
        </div>
      </>}
      {panel === "cancel" && <div className="ma-bye">
        <svg className="ma-bye-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d={HEART} width={3.2} /></svg>
        <h4 className="ma-panel-title" tabIndex={-1} data-autofocus>Before you go…</h4>
        <p className="ma-bye-text">No hard feelings, truly. Life gets busy and sewing comes in seasons. If you have a second, tell me why so I can make the club better.</p>
        <fieldset className="ma-reasons">
          <legend className="ma-legend">Why are you leaving? <span className="ma-optional">optional</span></legend>
          {["Taking a break from sewing", "It’s more than I want to spend", "The class times don’t work", "I didn’t use it much", "Something else"].map(r =>
            <label key={r} className={`ma-reason${reason === r ? " is-on" : ""}`}><input type="radio" name="ma-reason" value={r} checked={reason === r} onChange={() => setReason(r)} /><span>{r}</span></label>)}
        </fieldset>
        {reason && <div className="ma-field">
          <label htmlFor="ma-why" className="ma-field-label">Anything you’d like Chelsea to know? <span className="ma-optional">optional</span></label>
          <textarea id="ma-why" className="ma-input ma-textarea" rows={2} value={note} onChange={e => setNote(e.target.value)} />
        </div>}
        <div className="ma-stay">
          <p><b>Other options:</b> {plan === "member" ? `keep the posts for ${PLANS.friend.per}, or ` : ""}take a month off and come back on {PAUSE_UNTIL}.</p>
          <div className="ma-actions ma-actions-tight">
            {plan === "member" && <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => { setBill({ plan, comp, pending: "friend" }); toast(`You’ll switch to ${PLANS.friend.name} on ${NEXT}.`); close(); }}>Switch to {PLANS.friend.name}</button>}
            {!bill.paused && <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={pause}>Pause a month instead</button>}
          </div>
        </div>
        <p className="ma-panel-note">If you cancel, <b>you keep access until {NEXT}</b> and you won’t be charged again.</p>
        <div className="ma-actions">
          <button type="button" className="mb-btn mb-btn-ink" onClick={close}>Keep my {plan === "member" ? "membership" : "plan"}</button>
          <button type="button" className="mb-btn mb-btn-line ma-danger-line" onClick={cancel}>Cancel membership</button>
        </div>
      </div>}
    </div>
  </Reveal>;
}

function Invoices() {
  const { bill, toast } = useStore();
  const p = PLANS[bill.plan];
  const rows = [
    ...(bill.comp ? [{ date: "Oct 3, 2026", what: `${p.name} · complimentary`, amount: 0, status: "Comp" }] : [{ date: "Oct 3, 2026", what: `${p.name} · Oct 3 – Nov 3`, amount: p.price, status: "Paid" }]),
    { date: "Sep 3, 2026", what: `${p.name} · Sep 3 – Oct 3`, amount: p.price, status: "Paid" },
    { date: "Aug 3, 2026", what: `${p.name} · Aug 3 – Sep 3`, amount: p.price, status: "Paid" },
    { date: "Jul 3, 2026", what: `${p.name} · first month here`, amount: p.price, status: "Paid" },
  ];
  const file = (d: string) => `LT-${ME.no}-${new Date(d).toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
  return <Block title="Receipts" aside={<span className="ma-quiet">Older Patreon receipts stay in your Patreon account.</span>}>
    <table className="ma-table">
      <thead><tr><th scope="col">Receipt</th><th scope="col">Amount</th><th scope="col"><span className="mb-sr">Download</span></th></tr></thead>
      <tbody>{rows.map(r => <tr key={r.date}>
        <td><b>{r.date}</b><span>{r.what} · {r.status}</span></td>
        <td className="ma-num">{money(r.amount)}</td>
        <td className="ma-dl"><button type="button" className="mb-btn mb-btn-line mb-btn-sm" aria-label={`Download receipt for ${r.date} (PDF)`} onClick={() => toast(`Prototype: ${file(r.date)} would download.`)}><FileText size={15} aria-hidden="true" /> PDF</button></td>
      </tr>)}</tbody>
    </table>
  </Block>;
}

/** Chelsea's version: payouts, and where member management lives. */
function OwnerBilling() {
  const { toast } = useStore();
  const stats = [{ n: "412", l: "members" }, { n: "96", l: "Thread Friends" }, { n: "$10,588", l: "a month" }];
  return <>
    <div className="ma-ticket ma-owner">
      <div className="ma-ticket-stub" aria-hidden="true"><b>508</b><span>in the club</span></div>
      <div className="ma-ticket-body">
        <div className="ma-ticket-top"><p className="ma-kick">Club owner</p><span className="ma-chip">Payouts on</span></div>
        <h4 className="ma-plan-name">Loose Threads Club</h4>
        <ul className="ma-stats">{stats.map(s => <li key={s.l}><b>{s.n}</b><span>{s.l}</span></li>)}</ul>
        <dl className="ma-bill">
          <div><dt>Next payout</dt><dd>Oct 15, 2026</dd></div>
          <div><dt>Paid to</dt><dd>Checking ending 0917</dd></div>
          <div><dt>Plans</dt><dd>{PLANS.member.name} {PLANS.member.per} · {PLANS.friend.name} {PLANS.friend.per}</dd></div>
        </dl>
        <div className="ma-ticket-actions">
          <button type="button" className="mb-btn mb-btn-ink" onClick={() => toast("Prototype: this opens the Stripe dashboard.")}><ExternalLink size={16} aria-hidden="true" /> Open Stripe dashboard</button>
          <Link to={withRole(M.account, "member")} reloadDocument className="mb-btn mb-btn-line">See a member’s settings</Link>
        </div>
        <p className="ma-hint ma-hint-tight">Sample numbers for the prototype.</p>
      </div>
    </div>
    <button type="button" className="ma-admin" onClick={() => toast("Prototype: the club admin isn’t part of this prototype yet.")}>
      <span className="ma-admin-icon" aria-hidden="true"><Users size={22} /></span>
      <span className="ma-admin-text">
        <b>Members & roles (admin) <ArrowRight size={15} aria-hidden="true" /></b>
        <span>Complimentary access, staff roles (moderators, helpers) and removing members are managed in the club admin, not in your settings.</span>
      </span>
    </button>
  </>;
}

function BillingSection({ chelsea }: { chelsea: boolean }) {
  if (chelsea) return <Section id="billing" title="Club & payouts" lede="You don’t pay for your own club. Here’s what comes in, and where to manage members.">
    <OwnerBilling />
  </Section>;
  return <Section id="billing" title="Membership & billing" lede={<>Your plan, payments and receipts. Payments run through Stripe; we never see your full card number.</>}>
    <PlanTicket />
    <PlanPanel />
    <Invoices />
  </Section>;
}

/* ─── 3. Notifications ─── */

type NKey = "posts" | "digest" | "joins" | "day" | "hour" | "replies" | "mentions";
type Chan = { email: boolean; site: boolean | null }; // null: not offered on that channel
const N_INFO: Record<NKey, { label: string; hint: string }> = {
  posts: { label: "New posts", hint: "When Chelsea posts something new" },
  digest: { label: "Weekly digest", hint: "Sunday morning: the week’s posts, replays and favorite makes" },
  joins: { label: "New members", hint: "Who joined, upgraded or left, once a day" },
  day: { label: "1 day before", hint: "The night before, with the supply list" },
  hour: { label: "1 hour before", hint: "With the link to join the room" },
  replies: { label: "Replies to my comments", hint: "When someone answers you, or comments on your make" },
  mentions: { label: "Mentions", hint: "When someone @mentions you" },
};

function NotificationsSection({ chelsea, friend }: { chelsea: boolean; friend: boolean }) {
  const [n, setN] = useState<Record<NKey, Chan>>({
    posts: { email: true, site: true }, digest: { email: false, site: null }, joins: { email: true, site: true },
    day: { email: true, site: true }, hour: { email: true, site: true }, replies: { email: false, site: true }, mentions: { email: true, site: true },
  });
  const [saved, setSaved] = useState("");
  const { email } = useStore();
  const set = (k: NKey, c: "email" | "site") => (v: boolean) => {
    setN(p => ({ ...p, [k]: { ...p[k], [c]: v } }));
    const what = k === "day" || k === "hour" ? `Class reminder ${N_INFO[k].label}` : N_INFO[k].label;
    setSaved(`Saved. ${what}, ${c === "email" ? "email" : "on site"}: ${v ? "on" : "off"}.`);
  };
  const off = () => { setN(p => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, { ...v, email: false }])) as Record<NKey, Chan>); setSaved("Saved. All club emails are off. On-site notifications are unchanged."); };
  const groups: { title: string; keys: NKey[]; lock?: boolean }[] = [
    { title: "From the club", keys: chelsea ? ["posts", "digest", "joins"] : ["posts", "digest"] },
    { title: "Live class reminders", keys: ["day", "hour"], lock: friend },
    { title: "Conversations", keys: ["replies", "mentions"] },
  ];
  const ids = useId();
  const emailsOn = Object.entries(n).filter(([k, v]) => v.email && (chelsea || k !== "joins") && !(friend && (k === "day" || k === "hour"))).length;
  return <Section id="notifications" title="Notifications" lede="Pick what reaches you, and where: email, or the bell on the site.">
    <div className="ma-emails-to">
      <Mail size={18} aria-hidden="true" />
      <p>Emails go to <b>{email}</b></p>
      <span className="ma-count">{emailsOn} email{emailsOn === 1 ? "" : "s"} on</span>
    </div>
    <div className="ma-ntable">
      <div className="ma-nrow ma-nhead" aria-hidden="true"><span /><span id={`${ids}-email`}>Email</span><span id={`${ids}-site`}>On site</span></div>
      {groups.map(g => <div key={g.title} className="ma-ngroup" role="group" aria-labelledby={`${ids}-${g.title.replace(/\W/g, "")}`}>
        <h4 id={`${ids}-${g.title.replace(/\W/g, "")}`} className="ma-group-title">{g.title}</h4>
        {g.lock && <LockedRow need="members" className="ma-nlock" />}
        {g.keys.map(k => { const rid = `${ids}-${k}`; const c = n[k]; const dis = !!g.lock;
          return <div key={k} className={`ma-nrow${dis ? " is-off" : ""}`}>
            <div className="ma-pref-text">
              <span id={`${rid}-l`} className="ma-pref-label">{g.title === "Live class reminders" && <span className="mb-sr">Class reminder, </span>}{N_INFO[k].label}</span>
              <span id={`${rid}-h`} className="ma-pref-hint">{N_INFO[k].hint}</span>
            </div>
            <span className="ma-ncell"><span className="ma-ncell-l" aria-hidden="true">Email</span><Toggle small on={c.email} onChange={set(k, "email")} disabled={dis} labelledBy={`${rid}-l ${ids}-email`} describedBy={`${rid}-h`} /></span>
            <span className="ma-ncell">{c.site === null
              ? <span className="ma-na" title="Email only">—<span className="mb-sr">{N_INFO[k].label} is email only</span></span>
              : <><span className="ma-ncell-l" aria-hidden="true">On site</span><Toggle small on={c.site} onChange={set(k, "site")} disabled={dis} labelledBy={`${rid}-l ${ids}-site`} /></>}</span>
          </div>; })}
      </div>)}
    </div>
    <div className="ma-nfoot">
      <Saved text={saved} />
      <button type="button" className="ma-link" onClick={off} disabled={emailsOn === 0}>Turn off all emails</button>
    </div>
    <p className="ma-hint ma-hint-tight">Receipts and sign-in links always come by email, whatever you pick here.</p>
  </Section>;
}

/* ─── 4. Privacy & community ─── */

function PrivacySection({ chelsea, friend }: { chelsea: boolean; friend: boolean }) {
  const { privacy, setPrivacy, profile, toast } = useStore();
  const [blocked, setBlocked] = useState(BLOCKED);
  const [saved, setSaved] = useState("");
  const set = (k: keyof Privacy, label: string) => (v: boolean) => { setPrivacy({ ...privacy, [k]: v }); setSaved(`Saved. ${label}: ${v ? "on" : "off"}.`); };
  const chatAs = privacy.chatName ? profile.name : initials(profile.name);
  const lock = <LockedRow need="members" className="ma-pref-lock" />;
  return <Section id="privacy" title="Privacy & community" lede="What other members can see, and who you’d rather not hear from.">
    <Block title="Your visibility">
      <ul className="ma-prefs">
        {!chelsea && <SwitchRow label="Show my makes to other members" hint="Your makes appear on the makes wall and your profile. Off: only you and Chelsea see them."
          on={privacy.makes} onChange={set("makes", "Show my makes")} disabled={friend} extra={friend ? lock : undefined} />}
        {!chelsea && <SwitchRow label="Show my name in live chat" hint={<>Off shows your initials instead. In chat you’ll appear as <b className="ma-chatas">{chatAs}</b></>}
          on={privacy.chatName} onChange={set("chatName", "Name in live chat")} disabled={friend} extra={friend ? lock : undefined} />}
        <SwitchRow label="Let members see my profile" hint={chelsea ? "Your profile is always public to members as the club’s host." : "Your photo, bio and makes. Off: people see your name and photo only."}
          on={chelsea ? true : privacy.profile} onChange={set("profile", "Profile visible to members")} disabled={chelsea} />
      </ul>
      <Saved text={saved} />
    </Block>
    <Block title="Blocked members" aside={<span className="ma-quiet">{blocked.length} blocked</span>}>
      <p className="ma-hint ma-hint-tight">Blocked members can’t reply to you or mention you, and you won’t see their comments. They aren’t told. {chelsea && "To remove someone from the club, use the admin."}</p>
      {blocked.length
        ? <ul className="ma-list">{blocked.map(b => <li key={b.id} className="ma-list-row">
            <Avatar m={b} size={40} />
            <span className="ma-list-text"><b>{b.name}</b><span>{b.when}</span></span>
            <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => { setBlocked(l => l.filter(x => x.id !== b.id)); toast(`Unblocked ${b.name.replace(/\.$/, "")}. You’ll see their comments again.`); }}>Unblock<span className="mb-sr"> {b.name}</span></button>
          </li>)}</ul>
        : <p className="ma-empty">No one is blocked. To block someone, use the ⋯ menu on one of their comments.</p>}
    </Block>
  </Section>;
}

/* ─── 5. Login & security ─── */

function EmailChange({ email, setEmail }: { email: string; setEmail: (e: string) => void }) {
  const { toast } = useStore();
  const [step, setStep] = useState<"idle" | "enter" | "verify">("idle");
  const [next, setNext] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const ref = useAutofocus<HTMLDivElement>(step !== "idle");
  const btn = useRef<HTMLButtonElement>(null);
  const reset = () => { setStep("idle"); setNext(""); setCode(""); setErr(""); requestAnimationFrame(() => btn.current?.focus()); };
  const send = (e: React.FormEvent) => {
    e.preventDefault();
    const v = next.trim();
    if (!/^\S+@\S+\.\S+$/.test(v)) return setErr("That email doesn’t look quite right.");
    if (v.toLowerCase() === email.toLowerCase()) return setErr("That’s already your email.");
    setErr(""); setCode(""); setStep("verify");
  };
  const verify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) return setErr("Enter the 6-digit code from the email.");
    setEmail(next.trim()); toast(`Email changed to ${next.trim()}. We let your old address know too.`); reset();
  };
  return <div className="ma-row-item">
    <div className="ma-row-main">
      <span className="ma-row-label">Email</span>
      <span className="ma-row-value">{email}</span>
      <span className="ma-row-hint">Sign-in links, receipts and club emails go here.</span>
    </div>
    {step === "idle" && <button ref={btn} type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => setStep("enter")}>Change email</button>}
    <Reveal open={step !== "idle"}>
      <div ref={ref} className="ma-panel-in ma-panel-form">
        {step === "enter" && <form onSubmit={send} noValidate>
          <label htmlFor="ma-new-email" className="ma-field-label" data-autofocus tabIndex={-1}>New email</label>
          <input id="ma-new-email" type="email" autoComplete="email" inputMode="email" className="ma-input" value={next} aria-invalid={!!err} aria-describedby="ma-new-email-h" onChange={e => setNext(e.target.value)} />
          {err ? <p id="ma-new-email-h" className="ma-err" role="alert">{err}</p> : <p id="ma-new-email-h" className="ma-hint">We’ll send a 6-digit code there to make sure it’s yours.</p>}
          <div className="ma-actions"><button type="submit" className="mb-btn mb-btn-ink mb-btn-sm">Send code</button><button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={reset}>Cancel</button></div>
        </form>}
        {step === "verify" && <form onSubmit={verify} noValidate>
          <p className="ma-step-kick" data-autofocus tabIndex={-1}>Step 2 of 2 · Check <b>{next.trim()}</b></p>
          <label htmlFor="ma-code" className="ma-field-label">6-digit code</label>
          <input id="ma-code" className="ma-input ma-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} aria-invalid={!!err} aria-describedby="ma-code-h" onChange={e => setCode(e.target.value.replace(/\D/g, ""))} />
          {err ? <p id="ma-code-h" className="ma-err" role="alert">{err}</p> : <p id="ma-code-h" className="ma-hint">Prototype: no email is sent, so any 6 digits work. Your email doesn’t change until you confirm.</p>}
          <div className="ma-actions">
            <button type="submit" className="mb-btn mb-btn-ink mb-btn-sm">Confirm new email</button>
            <button type="button" className="ma-link" onClick={() => toast(`Prototype: a new code would go to ${next.trim()}.`)}>Resend code</button>
            <button type="button" className="ma-link" onClick={() => { setErr(""); setStep("enter"); }}>Use a different email</button>
          </div>
        </form>}
      </div>
    </Reveal>
  </div>;
}

function PasswordChange() {
  const { toast } = useStore();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ cur: "", next: "", again: "" });
  const [err, setErr] = useState("");
  const [changed, setChanged] = useState("March 2025");
  const ref = useAutofocus<HTMLDivElement>(open);
  const btn = useRef<HTMLButtonElement>(null);
  const close = () => { setOpen(false); setF({ cur: "", next: "", again: "" }); setErr(""); requestAnimationFrame(() => btn.current?.focus()); };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.cur) return setErr("Enter your current password.");
    if (f.next.length < 10) return setErr("Use at least 10 characters. A short sentence works well.");
    if (f.next !== f.again) return setErr("The new passwords don’t match.");
    setChanged(`today (${today()})`); toast("Password changed. Your other devices stay signed in."); close();
  };
  const strength = f.next.length === 0 ? "" : f.next.length < 10 ? "Too short" : f.next.length < 14 ? "Good" : "Strong";
  return <div className="ma-row-item">
    <div className="ma-row-main">
      <span className="ma-row-label">Password</span>
      <span className="ma-row-value">Last changed {changed}</span>
      <span className="ma-row-hint">Optional: you can always sign in with an email link instead.</span>
    </div>
    {!open && <button ref={btn} type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => setOpen(true)}>Change password</button>}
    <Reveal open={open}>
      <div ref={ref} className="ma-panel-in ma-panel-form">
        <form onSubmit={submit} noValidate className="ma-pw">
          <div className="ma-field">
            <label htmlFor="ma-pw-cur" className="ma-field-label">Current password</label>
            <input id="ma-pw-cur" data-autofocus type="password" autoComplete="current-password" className="ma-input" value={f.cur} onChange={e => setF({ ...f, cur: e.target.value })} />
          </div>
          <div className="ma-field">
            <label htmlFor="ma-pw-new" className="ma-field-label">New password {strength && <span className={`ma-strength ma-strength-${strength.split(" ")[0].toLowerCase()}`}>{strength}</span>}</label>
            <input id="ma-pw-new" type="password" autoComplete="new-password" className="ma-input" value={f.next} aria-describedby="ma-pw-h" onChange={e => setF({ ...f, next: e.target.value })} />
            <p id="ma-pw-h" className="ma-hint ma-hint-tight">At least 10 characters.</p>
          </div>
          <div className="ma-field">
            <label htmlFor="ma-pw-again" className="ma-field-label">New password again</label>
            <input id="ma-pw-again" type="password" autoComplete="new-password" className="ma-input" value={f.again} onChange={e => setF({ ...f, again: e.target.value })} />
          </div>
          {err && <p className="ma-err" role="alert">{err}</p>}
          <div className="ma-actions"><button type="submit" className="mb-btn mb-btn-ink mb-btn-sm">Change password</button><button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={close}>Cancel</button></div>
        </form>
      </div>
    </Reveal>
  </div>;
}

function Devices() {
  const { toast } = useStore();
  const [list, setList] = useState(DEVICES);
  const [confirm, setConfirm] = useState(false);
  const others = list.filter(d => !d.current);
  const Icon = (k: Device["kind"]) => (k === "phone" ? Smartphone : k === "tablet" ? Tablet : Monitor);
  return <Block title="Signed-in devices" aside={<span className="ma-quiet">{list.length} device{list.length === 1 ? "" : "s"}</span>}>
    <ul className="ma-list">{list.map(d => { const I = Icon(d.kind);
      return <li key={d.id} className="ma-list-row">
        <span className="ma-dev-icon" aria-hidden="true"><I size={20} /></span>
        <span className="ma-list-text"><b>{d.name}</b><span>{d.where} · {d.when}</span></span>
        {d.current ? <span className="ma-chip">This device</span>
          : <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => { setList(l => l.filter(x => x.id !== d.id)); toast(`Signed out ${d.name}.`); }}><LogOut size={14} aria-hidden="true" /> Sign out<span className="mb-sr"> {d.name}</span></button>}
      </li>; })}</ul>
    {others.length > 0 && (confirm
      ? <div className="ma-confirm" role="group" aria-label="Confirm signing out everywhere else">
          <p>Sign out {others.length} other device{others.length === 1 ? "" : "s"}? You’ll stay signed in here.</p>
          <div className="ma-actions ma-actions-tight">
            <button type="button" className="mb-btn mb-btn-ink mb-btn-sm" autoFocus onClick={() => { setList(l => l.filter(d => d.current)); setConfirm(false); toast(`Signed out of ${others.length} other device${others.length === 1 ? "" : "s"}.`); }}>Yes, sign them out</button>
            <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => setConfirm(false)}>Cancel</button>
          </div>
        </div>
      : <button type="button" className="ma-link ma-link-gap" onClick={() => setConfirm(true)}><LogOut size={14} aria-hidden="true" /> Sign out everywhere else</button>)}
  </Block>;
}

function SecuritySection() {
  const { email, setEmail } = useStore();
  return <Section id="security" title="Login & security" lede="Your sign-in email, password and the devices you’re signed in on.">
    <Block title="Sign-in">
      <EmailChange email={email} setEmail={setEmail} />
      <PasswordChange />
    </Block>
    <Devices />
  </Section>;
}

/* ─── 6. Your data ─── */

function DataSection({ who, chelsea }: { who: Viewer; chelsea: boolean }) {
  const { bill, email, toast } = useStore();
  const [requested, setRequested] = useState(false);
  const [step, setStep] = useState<"idle" | "confirm" | "done">("idle");
  const [typed, setTyped] = useState("");
  const ref = useAutofocus<HTMLDivElement>(step !== "idle");
  const btn = useRef<HTMLButtonElement>(null);
  const plan = PLANS[bill.plan];
  return <Section id="data" title="Your data" lede="Take a copy of everything, or close your account.">
    <Block title="Download my data">
      <p className="ma-hint ma-hint-tight">One zip with:</p>
      <ul className="ma-includes ma-includes-sm">
        {["Your posts and comments", "Your makes and their photos", "Billing history and receipts", "Your profile and settings"].map(t => <li key={t}><Check size={15} strokeWidth={3} aria-hidden="true" /> {t}</li>)}
      </ul>
      <div className="ma-actions">
        <button type="button" className="mb-btn mb-btn-ink" disabled={requested} onClick={() => { setRequested(true); toast(`We’ll email a download link to ${email}, usually within an hour.`); }}>
          <Download size={16} aria-hidden="true" /> {requested ? "Requested" : "Download my data"}
        </button>
        {requested && <span className="ma-quiet" role="status">Requested {today()}. The link works for 7 days.</span>}
      </div>
    </Block>
    <Block title="Delete account" className="ma-danger-block">
      {chelsea
        ? <p className="ma-hint ma-hint-tight">The owner account can’t be deleted from settings. If you ever want to close the club, that happens in the admin with support’s help, so members get notice and refunds.</p>
        : step === "done"
          ? <div className="ma-deleted" role="status">
              <p><b>Your account is scheduled for deletion on {new Date(Date.now() + 14 * DAY).toLocaleDateString("en-US", { month: "short", day: "numeric" })}.</b> Your subscription is canceled and you won’t be charged again. Changed your mind? You have 14 days.</p>
              <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={() => { setStep("idle"); setTyped(""); toast("Phew! Your account is restored."); requestAnimationFrame(() => btn.current?.focus()); }}><Undo2 size={14} aria-hidden="true" /> Keep my account</button>
            </div>
          : <>
              <ul className="ma-warn">
                <li><TriangleAlert size={15} aria-hidden="true" /> {bill.comp ? "Your complimentary access ends right away." : `Your ${plan.name} subscription (${plan.per}) cancels right away and you won’t be charged again.`}</li>
                <li><TriangleAlert size={15} aria-hidden="true" /> Your profile, makes and photos are removed. Comments stay, signed “Former member”.</li>
                {who.founding && <li><TriangleAlert size={15} aria-hidden="true" /> Your {FOUNDING} badge (No. {who.no}) can’t be brought back.</li>}
              </ul>
              {step === "idle" && <button ref={btn} type="button" className="mb-btn mb-btn-line ma-danger-line" onClick={() => setStep("confirm")}><Trash2 size={15} aria-hidden="true" /> Delete my account…</button>}
              <Reveal open={step === "confirm"}>
                <div ref={ref} className="ma-panel-in ma-panel-danger">
                  <form onSubmit={e => { e.preventDefault(); if (typed === "DELETE") { setStep("done"); toast("Account scheduled for deletion. We’ve emailed you a copy of this."); } }}>
                    <label htmlFor="ma-del" className="ma-field-label" data-autofocus tabIndex={-1}>Type <b>DELETE</b> to confirm</label>
                    <input id="ma-del" className="ma-input" value={typed} autoComplete="off" spellCheck={false} aria-describedby="ma-del-h" onChange={e => setTyped(e.target.value)} />
                    <p id="ma-del-h" className="ma-hint ma-hint-tight">Capital letters. You’ll have 14 days to change your mind.</p>
                    <div className="ma-actions">
                      <button type="submit" className="mb-btn ma-danger" disabled={typed !== "DELETE"}>Delete my account</button>
                      <button type="button" className="mb-btn mb-btn-line" onClick={() => { setStep("idle"); setTyped(""); requestAnimationFrame(() => btn.current?.focus()); }}>Keep my account</button>
                    </div>
                  </form>
                </div>
              </Reveal>
            </>}
    </Block>
  </Section>;
}

/* ─── Visitors ─── */

function VisitorBody() {
  return <Part p="faves">
    <section className="b2-sec ma-sec ma-visit" aria-labelledby="ma-visit-title">
      <div className="b2-wrap ma-visit-grid">
        <div>
          <Head id="ma-visit-title" hand="not in yet?" word="hi!" tone="pink" className="mb-headsm" />
          <p className="b2-lede">The Loose Threads Club is where we sew together: two live classes a month, every replay, and a wall full of everyone’s makes.</p>
          <JoinButton className="ma-visit-join" />
        </div>
        <ul className="ma-visit-cards">
          <li className="ma-visit-card">
            <p className="ma-kick">{PLANS.member.name}</p>
            <p className="ma-visit-price">${PLANS.member.price}<small>/mo</small></p>
            <p>Live classes, replays, makes and challenges.</p>
          </li>
          <li className="ma-visit-card ma-visit-card-friend">
            <p className="ma-kick">{PLANS.friend.name}</p>
            <p className="ma-visit-price">${PLANS.friend.price}<small>/mo</small></p>
            <p>Support the channel, read the posts, browse makes.</p>
          </li>
        </ul>
      </div>
      <div className="b2-wrap"><p className="ma-visit-yt b2-note">or catch me free on YouTube most Tuesdays, 7 PM MT!</p></div>
    </section>
  </Part>;
}

/* ─── Page ─── */

function Settings() {
  const role = useRole();
  const active = useActiveSection();
  const { bill, setBill } = useStore();
  const chelsea = role === "chelsea";
  const friend = role === "friend";
  const who = viewer(role)!;
  return <Part p="faves">
    <section className="b2-sec ma-sec ma-settings" aria-labelledby="ma-settings-title">
      <div className="b2-wrap">
        <div className="b2-row ma-row">
          <Head id="ma-settings-title" hand={chelsea ? "your club's" : "make it"} word={chelsea ? "settings" : "yours"} tone="blue" className="mb-headsm" />
          <div className="b2-row-aside ma-row-aside">
            <p className="b2-note">change anything, anytime</p>
            <svg className="b2-arrow" viewBox="0 0 80 60" aria-hidden="true"><Ink d="M70 6 C 56 30, 36 44, 12 48" delay={0.5} /><Ink d="M26 36 L11 48 L26 58" delay={0.9} /></svg>
          </div>
        </div>
        <div className="ma-set-grid">
          <SettingsNav active={active} chelsea={chelsea} />
          <div className="ma-content">
            {friend && <UpgradeCard />}
            <ProfileSection who={who} chelsea={chelsea} />
            <BillingSection chelsea={chelsea} />
            <NotificationsSection chelsea={chelsea} friend={friend && bill.plan === "friend"} />
            <PrivacySection chelsea={chelsea} friend={friend && bill.plan === "friend"} />
            <SecuritySection />
            <DataSection who={who} chelsea={chelsea} />
          </div>
        </div>
      </div>
    </section>
    {!chelsea && <ReviewControls label="Billing">
      <button type="button" aria-pressed={!bill.comp} onClick={() => setBill({ plan: bill.plan, comp: false })}>Paid</button>
      <button type="button" aria-pressed={bill.comp} onClick={() => setBill({ plan: bill.plan, comp: true })}>Complimentary</button>
    </ReviewControls>}
  </Part>;
}

/** Resets the sample person, plan and panels when the review role changes. */
function RoleSync() {
  const role = useRole();
  const { setProfile, setBill, setPanel, setEmail } = useStore();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const who = viewer(role);
    if (who) { setProfile(initProfile(who)); setEmail(who.email); }
    setBill(initBill(role)); setPanel("idle");
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function Body() {
  const role = useRole();
  return <>
    <RoleSync />
    {role === "visitor" ? <VisitorBody /> : <Settings />}
  </>;
}

export function MembersAccount() {
  const [profile, setProfile] = useState<Profile>(() => initProfile(viewer(roleFromUrl()) ?? ME));
  const [bill, setBill] = useState<Bill>(() => initBill(roleFromUrl()));
  const [panel, setPanel] = useState<PanelKey>("idle");
  const [privacy, setPrivacy] = useState<Privacy>({ makes: true, chatName: true, profile: true });
  const [email, setEmail] = useState(() => (viewer(roleFromUrl()) ?? ME).email);
  const { show, node } = useToast();
  const reduce = useReducedMotion();
  const goTo = useCallback((id: SectionId) => {
    const el = document.getElementById(id); if (!el) return;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    el.querySelector<HTMLElement>(".ma-set-title")?.focus({ preventScroll: true });
    const url = new URL(window.location.href); url.hash = id;
    window.history.replaceState(window.history.state, "", url);
  }, [reduce]);
  // A #section in the URL on first load jumps there once the page has laid out.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!SECTIONS.some(s => s.id === id)) return;
    const t = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start" }), 300);
    return () => window.clearTimeout(t);
  }, []);
  // The sticky club tabs' height, so the settings nav and section jumps sit under them.
  useEffect(() => {
    const tabs = document.querySelector<HTMLElement>(".mb-tabs"); if (!tabs) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty("--ma-stick", `${Math.ceil(tabs.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set); ro.observe(tabs);
    return () => { ro.disconnect(); root.style.removeProperty("--ma-stick"); };
  }, []);
  const store: Store = { profile, setProfile, bill, setBill, panel, setPanel, privacy, setPrivacy, email, setEmail, toast: show, goTo };
  return <S.Provider value={store}>
    <MembersPage page="account" header={<Header />}>
      <Body />
      {node}
    </MembersPage>
  </S.Provider>;
}
